// @givmo/app-admin — onboarding wizard step 1 (required per-employee budget).
//
// Ports the Flutter `OnboardingStepOne`: welcome copy + the per-employee match budget money input.
// The input is whole-dollar masked (`$x,xxx`, no decimals — Flutter `precision: 0`); on every
// change it reports the parsed integer (or `null` for 0/empty) up to the wizard. Verbatim copy.

import { MaskedInput } from '@givmo/design-system';
import { WHOLE_DOLLAR_MASK, formatWholeDollars, parseWholeDollars } from './onboarding-money';

export interface OnboardingStepOneProps {
  /** Current per-employee budget (integer dollars) or `null`. */
  budget: number | null;
  /** Report the parsed integer (or `null` when 0/empty). */
  onBudgetChange: (value: number | null) => void;
}

export function OnboardingStepOne({ budget, onBudgetChange }: OnboardingStepOneProps) {
  return (
    <div className="flex flex-col items-stretch" data-testid="onboarding-step-1">
      <h2 className="font-sans text-s20 font-w700 tracking-givmo text-text-strong">
        Welcome to Givmo!
      </h2>
      <p className="mt-8 font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary">
        Your first critical step is to set your match policy. We&apos;ll walk you through it in
        three quick steps — you can save and finish later from any screen.
      </p>

      <h3 className="mt-32 font-sans text-s16 font-w600 tracking-givmo text-text-strong">
        Per-employee match budget
      </h3>
      <p className="mt-8 font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary">
        How much will your company match per employee per calendar year?
      </p>

      <div className="mt-16">
        <MaskedInput
          imaskOptions={WHOLE_DOLLAR_MASK}
          value={formatWholeDollars(budget)}
          placeholder="$0"
          onAccept={(masked) => onBudgetChange(parseWholeDollars(masked))}
          aria-label="Per-employee match budget"
          testId="onboarding-budget-per-employee"
        />
      </div>
    </div>
  );
}
